import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineCalculate,
    MdOutlineLocationOn,    
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlinePrint,
    MdSuperscript
} from 'react-icons/md';
import { IoMdRadioButtonOn, IoMdRadioButtonOff } from "react-icons/io";
import { useReactToPrint } from 'react-to-print';
import Header from 'components/Header';
import Button from 'components/Button2';
import TextInput from 'components/TextInput2';
import ScientificCalculator from 'components/ScientificCalculator';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import { generatePassageHTML } from 'global/passageHighlight';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import { flattenKatexUnderlines } from 'global/katexWrap';

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];
const DISABLE_PRINT = ['ReadingTown', 'College Planning Corp', 'A+ Program', 'MT Learning Center', 'PREPARE Test Prep', 'High Peak Education'];

function TestPaperACTReview() {
    const [studentName, setStudentName] = useState('');
    const [testName, setTestName] = useState(null);
    const [testModules, setTestModules] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [calculator, setCalculator] = useState(null);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [annotations, setAnnotations] = useState([]);
    const [fullPassage, setFullPassage] = useState<{
        html?: string;
        preview?: string;
    } | null>(null);
    const [printFullPassage, setPrintFullPassage] = useState<Record<string, { html?: string; }>>({});
    const [highlighting, setHighlighting] = useState<Array<{
        passageId?: string;
        html?: string;
        preview?: string;
    }>>([]);
    const [print, setPrint] = useState(false);

    const { user } = useAuthContext();
    const printRef = useRef(null);
    const navigate = useNavigate();
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));
    const passageContainerRef = useRef(null);

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            // exit the review
            navigate('/tests');
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('testACTReview__passages');
        let question = document.getElementById('testACTReview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    const generateHightlightIndexing = ({id: passageId, indices, passageFigures = []}) => {
        for (let j = 0; j < indices.length; j++){
            const passage = document.getElementById(passageId);
            let passageHTML = '';
            let passagePreview = '';
            const usedFigureIndices = new Set();
            const { startIndex, endIndex } = indices[j];
            let startOffset = startIndex
            let endOffset = endIndex
            let lastIndex = 0;

            for (let i = 0; i < passage.childNodes.length; i++) {
                const childNode = passage.childNodes[i];

                const addMarkTags = (node) => {
                    if (node.outerHTML) {
                        const { innerHTML, outerHTML } = node;
                        const childOffset = outerHTML.indexOf('>') + 1;
    
                        passageHTML += outerHTML.slice(0, childOffset);
    
                        for (let i = 0; i < node.childNodes.length; i++) {
                            const child = node.childNodes[i];
                            addMarkTags(child);
                        }
    
                        passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                    }
                    else {
                        // 이미지 삽입을 위한 passageFigure 검사
                        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                        if (currentFigure) {
                            usedFigureIndices.add(currentFigure.figure.index);
                            passageHTML += `
                                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                            `;                    
                        }
                        const { textContent } = node;
    
                        if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                        }
                        else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(startOffset - lastIndex, textContent.length);
                            passageHTML += '</mark>';
                            passagePreview += textContent.slice(startOffset - lastIndex, textContent.length);
                        }
                        else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(0, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(0, endOffset - lastIndex);
                        }
                        else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                            passageHTML += '<mark>';
                            passageHTML += textContent;
                            passageHTML += '</mark>';
                            passagePreview += textContent;
                        }
                        else {
                            passageHTML += textContent;
                        }
    
                        lastIndex += textContent.length;
                    }
                }
    
                addMarkTags(childNode);
            }

            const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
            if (currentFigure) {
                passageHTML += `
                    <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                `;                    
            }

            setHighlighting(prev => [...(prev || []), { 
                passageId: passageId,
                html: passageHTML, 
                preview: passagePreview 
            }]);
        }

    };

    const getPassageWithFigure = ({id: passageId, passageFigures = []}) => {
        const passage = document.getElementById(passageId);
        let passageHTML = '';
        let lastIndex = 0;
        const usedFigureIndices = new Set();

        for (let i = 0; i < passage.childNodes.length; i++) {
            const childNode = passage.childNodes[i];
            // add open tags first while see the text
            const addMarkTags = (node) => {
                if (node.outerHTML) {
                    const { innerHTML, outerHTML } = node;
                    const childOffset = outerHTML.indexOf('>') + 1;

                    passageHTML += outerHTML.slice(0, childOffset);

                    for (let i = 0; i < node.childNodes.length; i++) {
                        const child = node.childNodes[i];
                        addMarkTags(child);
                    }

                    passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                } 
                else {
                    // 이미지 삽입을 위한 passageFigure 검사
                    const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                    if (currentFigure) {
                        usedFigureIndices.add(currentFigure.figure.index);
                        passageHTML += `
                            <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                        `;                    
                    }
                    const { textContent } = node;
                    passageHTML += textContent;
                    lastIndex += textContent.length;
                }  
            }

            addMarkTags(childNode);
        }

        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
        if (currentFigure) {
            passageHTML += `
                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
            `;                    
        }
        
        setFullPassage({
            html: passageHTML
        });

    };

    const getArrayPassageWFigure = () => {
        let newPassages = {}; // 새로운 `printFullPassage` 데이터를 저장할 객체
    
        for (let i = 0; i < testModules.length; i++) {
            const module = testModules[i];
    
            for (let j = 0; j < module.questions.length; j++) {
                const question = module.questions[j];
                if (question.passages) {
                    const id = question.passages.id;
                    const passageFigures = question.passages.passageFigures || [];
                    if (!newPassages[id]) {
                        const passage = document.getElementById(`print ${id}`);
                        let passageHTML = '';
                        let lastIndex = 0;
                        const usedFigureIndices = new Set();

                        for (let i = 0; i < passage.childNodes.length; i++) {
                            const childNode = passage.childNodes[i];
                            // add open tags first while see the text
                            const addMarkTags = (node) => {
                                if (node.outerHTML) {
                                    const { innerHTML, outerHTML } = node;
                                    const childOffset = outerHTML.indexOf('>') + 1;
                
                                    passageHTML += outerHTML.slice(0, childOffset);
                
                                    for (let i = 0; i < node.childNodes.length; i++) {
                                        const child = node.childNodes[i];
                                        addMarkTags(child);
                                    }
                
                                    passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                                } 
                                else {
                                    // 이미지 삽입을 위한 passageFigure 검사
                                    const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                                    if (currentFigure) {
                                        usedFigureIndices.add(currentFigure.figure.index);
                                        passageHTML += `
                                            <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                                        `;                    
                                    }
                                    const { textContent } = node;
                                    passageHTML += textContent;
                                    lastIndex += textContent.length;
                                }  
                            }
                
                            addMarkTags(childNode);
                        }
                
                        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                        if (currentFigure) {
                            passageHTML += `
                                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                            `;                    
                        }
            
                        newPassages[id] = {
                            html: passageHTML
                        };
                    }
                }
            }
        }
        setPrintFullPassage(newPassages); // 최종 업데이트
    };

    const generateAnnotation = () => {
        const newAnnotations = [ ...testModules[moduleIndex].answers[questionIndex].annotations ];

        for (let i = 0; i < newAnnotations.length; i++) {
            const {
                passageId,
                startIndex,
                endIndex
            } = newAnnotations[i];

            const passage = document.getElementById(`passage ${passageId}`);
            // Stem annotations reference a question id with no matching
            // passage element on the review page; skip this one rather than
            // abandoning the remaining annotations.
            if (!passage) continue;

            const { passageHTML, passagePreview } = generatePassageHTML(passage, startIndex, endIndex);

            newAnnotations[i].html = passageHTML;
            newAnnotations[i].preview = passagePreview;
        }

        setAnnotations(newAnnotations);
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            if (testModules[moduleIndex].subject === 'ACT English' || testModules[moduleIndex].subject === 'ACT Reading'|| testModules[moduleIndex].subject === 'ACT Science') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        if (!header) {
            return; 
        }
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        }

        const dragElement = (event) => {
            event.preventDefault();

            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        }

        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        }
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onAfterPrint: () => setPrint(false)
    });

    useEffect(() => {
        axios.get(`tests/digital/act/b2b/review/${testId}/`)
            .then(res => {
                setStudentName(res.data.studentName)
                setTestName(res.data.name)
                setTestModules(res.data.testModules);
                // console.log(res.data.testModules);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);
    
    // passage scroll, passage magnifier scroll (according to the highliting index)
    useLayoutEffect(() => {
        if (highlighting.length > 0) {
            const passageId = highlighting[0].passageId;
            const passage = document.getElementById(`highlighting ${passageId}`);
            // highlighting 여러개일 경우 첫 번째 
            if (passage) {
                let firstHighlight = passage.querySelector("mark"); // 첫 번째 mark 태그 찾기
                let offset = 0;
                while (firstHighlight !== passage) {
                    offset = firstHighlight.offsetTop;
                    firstHighlight = firstHighlight.parentElement;
                }
                if (firstHighlight && passageContainerRef.current) {
                    passageContainerRef.current.scrollTo({
                        top: firstHighlight? offset - passageContainerRef.current.offsetTop: 0,
                        behavior: "smooth", 
                    });
                }
            }
        }
    }, [highlighting]);

    useEffect(() => {
        if (testModules) {
            // let calculatorEl = document.getElementById('calculator');
            // setCalculator(Desmos.GraphingCalculator(calculatorEl, {
            //     notes: false,
            //     folders: false,
            //     images: false,
            //     links: false
            // }));

            // dragMove(document.getElementById('calculatorDrag'), document.getElementById('calculatorHeader'));
            const calculatorDragEl = document.getElementById('calculatorDrag');
            const calculatorHeaderEl = document.getElementById('calculatorHeader');
            if (calculatorDragEl && calculatorHeaderEl) {
                dragMove(calculatorDragEl, calculatorHeaderEl);
            }

            const explanationDragEl = document.getElementById('explanationDrag');
            const explanationHeaderEl = document.getElementById('explanationHeader');
            if (explanationDragEl && explanationHeaderEl) {
                dragMove(explanationDragEl, explanationHeaderEl);
            }
        }
    }, [testModules]);

    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
            setHighlighting([]);
            setFullPassage(null);
            const passageData = testModules[moduleIndex].questions[questionIndex]?.passages;
            if (passageData) {
                const { id, indices, passageFigures } = testModules[moduleIndex].questions[questionIndex].passages
                if (indices) generateHightlightIndexing({ id, indices, passageFigures });  
                getPassageWithFigure({ id, passageFigures });
            }
        }
    }, [testModules, moduleIndex, questionIndex]);

    useEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [directionsOpen]);

    useEffect(() => {
        if (fullPassage) {
            generateAnnotation();
        }
    }, [fullPassage]);

    // useEffect(() => {
    //     if (calculatorOpen) {
    //         calculator.focusFirstExpression();
    //     }
    // }, [calculatorOpen]);

    useEffect(() => {
        if (print) {
            renderKatex();
            getArrayPassageWFigure();
        }
    }, [print]);

    useEffect(() => {
        if (Object.keys(printFullPassage).length > 0) {
            handlePrint();
        }
    }, [printFullPassage]);

    if (testModules === null) return <Loader />;

    const {
        name,
        questions,
        answers,
        subject,
        prompt
    } = testModules[moduleIndex];

    return (
        <>
        <Helmet>
            <title>
                Test Review - Test Center Admin
            </title>
        </Helmet>
        {print && <div className='testACTReview__print' ref={printRef}>
            {testModules.map(testModule => (<>
                <h2 className='testACTReview__title'>
                    {testModule.subject.replace('ACT ', '')}
                </h2>
                {testModule.questions.map((question, q) => {
                    return (
                        <div className='testACTReview__test'>
                            {/* {question.passages && */}
                            {(question.passages && testModule.questions.findIndex(moduleQuestion => moduleQuestion.passages?.id === question.passages.id) === q) && 
                            <>
                                <div className='testACTReview__materialContent'>
                                    <div key={question.passages.id} className='testACTReview__passage'>
                                        <p
                                            id={`print ${question.passages.id}`}
                                            className='katex testACTReview__passageText'
                                        >
                                            {question.passages.passage}
                                        </p>
                                        {printFullPassage[question.passages.id] && <p
                                            id={`passage ${question.passages.id}`}
                                            className='katex testACTReview__passageTextPassage'
                                            dangerouslySetInnerHTML={{ __html: printFullPassage[question.passages.id].html || 'yes' }}
                                        />}
                                    </div>
                                </div>
                            </>}
                            <div className='testACTReview__question'>
                                <div className='testACTReview__questionContent'>
                                    <div className='testACTReview__questionHeader'>
                                        <p className={testModule.answers[q].isCorrect
                                            ? 'testACTReview__questionNumber--correct'
                                            : 'testACTReview__questionNumber--wrong'
                                        }>
                                            {q + 1}
                                        </p>
                                    </div>

                                    <div id='testACTReview__questionChoices' className='testACTReview__questionChoices'>
                                        {testModule.subject === 'ACT Mathematics' &&
                                            question.figures.map(figure => (
                                                <img
                                                    className='testACTReview__figure'
                                                    key={figure.id}
                                                    src={figure.image}
                                                    alt={(q + 1).toString()}
                                                />
                                            ))
                                        }
                                        <p className='testACTReview__questionQuestion'>
                                            <span className='katex'>
                                                {question.question}
                                            </span>
                                        </p>
                                        {question.choices.length
                                        &&  // multiple choice answer
                                        <>
                                                {question.choices.map((choice, i) => (
                                                    <div key={choice.id} className='testACTReview__questionChoiceWrapper'>
                                                        <div className='testACTReview__choiceContainer'>
                                                            <div
                                                                className={choice.isCorrect
                                                                    ? 'testACTReview__radioButton--correct'
                                                                    : testModule.answers[q].answer == choice.id
                                                                    ? 'testACTReview__radioButton--wrong'
                                                                    : 'testACTReview__radioButton'
                                                                }>
                                                                    {testModule.answers[q].answer == choice.id ?
                                                                        <IoMdRadioButtonOn className='testACTReview__radioButtonIcon'/>
                                                                        : <IoMdRadioButtonOff className='testACTReview__radioButtonIcon'/>
                                                                    }
                                                            </div>
                                                            <div className={choice.isCorrect
                                                                ? 'testACTReview__questionChoiceContent--correct'
                                                                : testModule.answers[q].answer == choice.id
                                                                ? 'testACTReview__questionChoiceContent--wrong'
                                                                : 'testACTReview__questionChoiceContent'
                                                            }>
                                                                <p className={choice.isCorrect
                                                                    ? 'testACTReview__choiceText--correct'
                                                                    : testModule.answers[q].answer == choice.id
                                                                    ? 'testACTReview__choiceText--wrong'
                                                                    : 'testACTReview__choiceText'    
                                                                }>
                                                                    {MULTIPLE_CHOICES[i]}.
                                                                </p>
                                                                {choice.figure
                                                                ?   <img
                                                                        className='testACTReview__questionChoiceFigure'
                                                                        key={choice.figure}
                                                                        src={choice.figure}
                                                                        alt={choice.figure}
                                                                    />
                                                                :   <p className='testACTReview__questionChoiceText'>
                                                                        <span className='katex'>
                                                                            {choice.choice}
                                                                        </span>
                                                                    </p>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        }
                                        <div className='testACTReview__marginBottom'></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </>))}
        </div>}
        <div className='testACTReview__box' onCopy={(event) => { event.preventDefault() }}>
            {/* screen that will be appear when click button is working */}
            <div className='testACTReview'>
                {/* test Header */}
                <div className='testACTReview__header'>
                    <div className='testACTReview__titleGroup'>
                        <button className='testACTReview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='testACTReview__title'>
                                {subject.replace('ACT ', '')}
                            </h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='testACTReview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='testACTReview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='testACTReview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule, t) => (
                                        <p
                                            className={t === moduleIndex
                                                ? 'testACTReview__moduleSelectDropdownItem--selected'
                                                : 'testACTReview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {testModule.subject.replace('ACT ' ,'')}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='testACTReview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='testACTReview__directionsIcon' />
                                : <MdKeyboardArrowDown className='testACTReview__directionsIcon' />
                            }
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='testACTReview__directions' ref={directionsRef}>
                            <div className='testACTReview__directionsBubble'>
                                <p className='katex testACTReview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='testACTReview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }

                    {/* header action */}
                    <div className='testACTReview__actionGroup'>
                        {!(user.organizations.some(organization => DISABLE_PRINT.includes(organization))
                            || user.customizations.find(customization => customization === 'Disable Print')
                        ) &&
                            <button
                                className='testACTReview__action'
                                onClick={() => setPrint(true)}
                            >
                                <MdOutlinePrint className='testACTReview__actionIcon' />
                                <p className='testACTReview__actionText'>Print</p>
                            </button>
                        }
                        {subject === 'ACT Mathematics' && <>
                            <button 
                                className={calculatorOpen
                                    ? 'testACTReview__action--active'
                                    : 'testACTReview__action'
                                }
                                onClick={() => {
                                    setCalculatorOpen(!calculatorOpen);
                                }}
                            >
                                <MdOutlineCalculate className='testACTReview__actionIcon' />
                                <p className='testACTReview__actionText'>Calculator</p>
                            </button>
                        </>}
                        <button
                            className='testACTReview__action'
                            onClick={() => navigate(-1)}
                        >
                            <MdExitToApp className='testACTReview__actionIcon' />
                            <p className='testACTReview__actionText'>Exit</p>
                        </button>
                    </div>
                    <div id='calculatorDrag' className={calculatorOpen
                        ? 'testACTReview__popup--calculator'
                        : 'testACTReview__popup--hidden'
                    }>
                        <div id='calculatorHeader' className='testACTReview__popupHeader'>
                            <h3 className='testACTReview__popupHeaderText'>Scientific Calculator</h3>
                            <MdClose className='testACTReview__popupHeaderIcon' onClick={() => setCalculatorOpen(false)} />
                        </div>
                        <ScientificCalculator />
                        {/* <div
                            id='calculator'
                            style={{ width: 400, height: 600 }}
                        ></div> */}
                    </div>
                    <div id='explanationDrag' className={showExplanation
                        ? 'testACTReview__popup--explanation'
                        : 'testACTReview__popup--hidden'
                    }>
                        <div id='explanationHeader' className='testACTReview__popupHeader'>
                            <h3 className='testACTReview__popupHeaderText'>Explanation</h3>
                            <MdClose className='testACTReview__popupHeaderIcon' onClick={() => setShowExplanation(false)} />
                        </div>
                        <div className='testACTReview__explanationContainer'>
                            {questions[questionIndex].explanationFigure && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <img
                                        src={questions[questionIndex].explanationFigure}
                                        alt='Explanation Figure'
                                        style={{ maxWidth: questions[questionIndex].explanation ? '80%' : '100%', height: 'auto' }}
                                    />
                                </div>
                            )}
                            <div className='testACTReview__explanationBox katex'>
                                {questions[questionIndex].explanation
                                    ? questions[questionIndex].explanation
                                    : questions[questionIndex].explanationFigure
                                    ? ''
                                    : 'No explanation'
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className='testACTReview__dottedLine'></div>


                {/* test content */}
                <div className='testACTReview__test'>
                    {/* left-passage */}
                    {questions[questionIndex].passages && <>
                        <div className='testACTReview__material' ref = {passageContainerRef}>
                            <div className='testACTReview__materialContent'>
                                {/* {questions[questionIndex].figures.map(figure => (
                                    <img
                                        className='testACTReview__figure'
                                        key={figure.id}
                                        src={figure.image}
                                        alt={(questionIndex + 1).toString()}
                                    />
                                ))} */}
                                <div key={questions[questionIndex].passages.id} className='testACTReview__passage'>
                                    {/* 해당 id를 가진 passage의 p태그 가져와서 highlight 넣은 html 받아와서 겹쳐 보여줌 (z-index, transparent 조절) */}
                                    <p
                                        id={questions[questionIndex].passages.id}
                                        className='katex testACTReview__passageText'
                                    >
                                        {questions[questionIndex].passages.passage}
                                    </p>
                                    <p
                                        id={`passage ${questions[questionIndex].passages.id}`}
                                        className='katex testACTReview__passageTextPassage'
                                        dangerouslySetInnerHTML={{ __html: fullPassage?.html }}
                                    >
                                    </p>
                                    {/* {questionNumber > 0 &&  */}
                                    {highlighting.length > 0 &&
                                        highlighting.map((highlight, index)=>
                                            <div
                                                id={`highlighting ${questions[questionIndex].passages.id}`}
                                                key={index}
                                                className='katex testACTReview__Highlight'
                                                dangerouslySetInnerHTML={{ __html: highlight?.html }}
                                            ></div>
                                        )
                                    }
                                    {
                                        answers[questionIndex].annotations.filter(annotation => (
                                            annotation.passageId === `${questions[questionIndex].passages.id}`
                                        )).map((annotation, a) => <>
                                            <div
                                                className='katex testACTReview__passageAnnotation'
                                                dangerouslySetInnerHTML={{ __html: annotation.html }}
                                            ></div>
                                        </>)
                                    }
                                </div>
                            </div>
                        </div>
                        <div className='testACTReview__verticalLine'></div>
                    </>}
                    
                    {/* right-question */}
                    <div className='testACTReview__question'>
                        <div className='testACTReview__questionContent'>
                            <div className='testACTReview__questionHeader'>
                                <p className={answers[questionIndex].isCorrect
                                    ? 'testACTReview__questionNumber--correct'
                                    : 'testACTReview__questionNumber--wrong'
                                }>
                                    {questionIndex + 1}
                                </p>
                                <button className='testACTReview__questionMarkButton' onClick={() => {}}>
                                    {answers[questionIndex].marked
                                        ? <MdOutlineBookmark className='testACTReview__questionMarkIcon--marked' />
                                        : <MdOutlineBookmarkBorder className='testACTReview__questionMarkIcon' />
                                    }
                                    Flag
                                </button>
                                {(questions[questionIndex].explanation || questions[questionIndex].explanationFigure) &&
                                    <button
                                        className={showExplanation
                                            ? 'testACTReview__explanation--active'
                                            : 'testACTReview__explanation'
                                        }
                                        onClick={() => setShowExplanation(!showExplanation)}
                                    >
                                        Explanation
                                    </button>
                                }
                                <button
                                    className={showAnswer
                                        ? 'testACTReview__showAnswer--active'
                                        : 'testACTReview__showAnswer'
                                    }
                                    onClick={() => setShowAnswer(!showAnswer)}
                                >
                                    {showAnswer ? 'Hide answer' : 'Show answer'}
                                </button>
                            </div>

                            <div id='testACTReview__questionChoices' className='testACTReview__questionChoices'>
                                {/* question content */}
                                <p className='testACTReview__questionQuestionContainer'>
                                    <span className='katex testACTReview__questionQuestion'>
                                        {questions[questionIndex].question}
                                    </span>
                                    {questions[questionIndex].figures &&
                                        questions[questionIndex].figures.map(figure => (
                                            <img
                                                className='testACTReview__figure'
                                                key={figure.id}
                                                src={figure.image}
                                                alt={(questionIndex + 1).toString()}
                                            />
                                        ))
                                    }
                                </p>
                                {questions[questionIndex].choices.length
                                ?   // multiple chioice
                                    <>
                                        {questions[questionIndex].choices.length &&   
                                            <>
                                                {questions[questionIndex].choices.map((choice, i) => (
                                                    <div key={choice.id}  
                                                        className='testACTReview__questionChoiceWrapper'
                                                    >
                                                        <div 
                                                            className='testACTReview__choiceContainer'
                                                        >
                                                            <div
                                                                className={showAnswer && choice.isCorrect
                                                                    ? 'testACTReview__radioButton--correct'
                                                                    : answers[questionIndex].answer == choice.id
                                                                    ? (choice.isCorrect 
                                                                        ? 'testACTReview__radioButton--selected'
                                                                        : 'testACTReview__radioButton--wrong'
                                                                    )
                                                                    : 'testACTReview__radioButton'
                                                                }>
                                                                    {showAnswer && choice.isCorrect || answers[questionIndex].answer == choice.id?
                                                                        <IoMdRadioButtonOn className='testACTReview__radioButtonIcon'/>
                                                                        : <IoMdRadioButtonOff className='testACTReview__radioButtonIcon'/>
                                                                    }
                                                            </div>
                                                            <div className={showAnswer && choice.isCorrect
                                                                ? 'testACTReview__questionChoiceContent--correct'
                                                                : answers[questionIndex].answer == choice.id
                                                                ? (choice.isCorrect 
                                                                    ? 'testACTReview__questionChoiceContent--selected'
                                                                    : 'testACTReview__questionChoiceContent--wrong'
                                                                )
                                                                : 'testACTReview__questionChoiceContent'
                                                            }>
                                                                <p className={showAnswer && choice.isCorrect
                                                                    ? 'testACTReview__choiceText--correct'
                                                                    : answers[questionIndex].answer == choice.id
                                                                    ? (choice.isCorrect 
                                                                        ? 'testACTReview__choiceText--selected'
                                                                        : 'testACTReview__choiceText--wrong'
                                                                    )
                                                                    : 'testACTReview__choiceText'
                                                                }>
                                                                    {MULTIPLE_CHOICES[i]}.
                                                                </p>
                                                                {choice.figure
                                                                ?   <img
                                                                        className='testACTReview__questionChoiceFigure'
                                                                        key={choice.figure}
                                                                        src={choice.figure}
                                                                        alt={choice.figure}
                                                                    />
                                                                :   <p className='testACTReview__questionChoiceText'>
                                                                        <span className='katex'>
                                                                            {choice.choice}
                                                                        </span>
                                                                    </p>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        }
                                    </>
                                : // input answer
                                   <div className='testACTReview__questionAnswerReview'>
                                        <TextInput
                                            className='testACTReview__questionAnswer'
                                            inputClassName='testACTReview__questionAnswerInput'
                                            name={name + questionIndex}
                                            value={showAnswer? questions[questionIndex].correctAnswers.toString().replaceAll(',', ', '): ''}
                                        />
                                    </div>
                                }
                                <div className='testACTReview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='testACTReview__dottedLine'></div>
                
                {/* text content bottom */}
                <div className='testACTReview__dottedLine'></div>
                <div className='testACTReview__footer'>
                    <p className='testACTReview__tester'>
                        {studentName}
                    </p>
                    <button
                        className='testACTReview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='testACTReview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='testACTReview__navigatorToggleIcon' />
                        }
                    </button>
                    <div className='testACTReview__buttons'>
                        <Button
                            className='testACTReview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='testACTReview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>
                    {navigatorOpen &&
                        <div className='testACTReview__navigator' ref={navigatorRef}>
                            <div className='testACTReview__navigatorBubble'>
                                <h2 className='testACTReview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='testACTReview__navigatorLegends'>
                                    <div className='testACTReview__navigatorLegend'>
                                        <MdOutlineLocationOn className='testACTReview__navigatorLegendCurrent' />
                                        <p className='testACTReview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                    <div className='testACTReview__navigatorLegend'>
                                        <div className='testACTReview__navigatorLegendCorrect'></div>
                                        <p className='testACTReview__navigatorLegendText'>
                                            Correct
                                        </p>
                                    </div>
                                    <div className='testACTReview__navigatorLegend'>
                                        <div className='testACTReview__navigatorLegendWrong'></div>
                                        <p className='testACTReview__navigatorLegendText'>
                                            Incorrect
                                        </p>
                                    </div>
                                    <div className='testACTReview__navigatorLegend'>
                                        <MdOutlineBookmark className='testACTReview__navigatorLegendMarked' />
                                        <p className='testACTReview__navigatorLegendText'>
                                            For review
                                        </p>
                                    </div>
                                </div>
                                <div className='testACTReview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className={answers[i].isCorrect
                                                ? 'testACTReview__navigatorItem--correct'
                                                : 'testACTReview__navigatorItem--wrong'
                                            }
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='testACTReview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='testACTReview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='testACTReview__navigatorItemCurrent' />}
                                            {answers[i].marked && <MdOutlineBookmark className='testACTReview__navigatorItemMarked' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
        </>
    );
}

export default TestPaperACTReview;